import { getCirclePickState } from "../../../runtime/circlePickState";
import { getPosPickState } from "../../../runtime/posPickState";
import { getVisualGeomOriginPickState } from "../../../runtime/visualGeomOriginPickState";
import { useSelectionStore } from "../../../stores/selectionStore";
import { useViewerStore } from "../../../stores/viewerStore";
import { focusNewEntityInInspector } from "../../../utils/focusNewEntityInInspector";
import { logger } from "../../../utils/logger";
import { pickScaleSelectionTargetFromIntersects } from "../../../utils/scaleSelection";
import { clearSelectedVisualMeshes } from "../../../visual-mesh-multi-selection";
import { applyConstraintMarkerKeySelection } from "../../scene/constraintMarkerVisibility";
import { getSavedSceneCameraPickUuid } from "../../scene/savedCameraMarker";
import { getSavedSceneLightPickUuid } from "../../scene/savedLightMarker";
import { devLog } from "../../scene/sceneConstants";
import {
  pickBestJointHit,
  pickJointCenterProxyHit,
  pickPriorityInspectTarget,
} from "../../scene/scenePicking";
import { getCirclePickHits, handleCirclePickClick } from "./circlePickInteraction";
import { handleMeasurementPickClick } from "./measurementPickInteraction";
import { commitPosPick } from "./posPickInteraction";
import { TARGET_HOVER_CURSOR } from "./scenePickerConstants";
import { filterScenePickerIntersects } from "./scenePickerFiltering";
import { clearSceneHoverPreviews } from "./scenePickerHoverPreviews";
import {
  commitVisualGeomOriginPick,
  getVisualGeomOriginPickHit,
} from "./visualGeomOriginPickInteraction";

function hidePosPickMarkerFromEnsure(ensurePosPickMarker) {
  try {
    const marker = ensurePosPickMarker();
    if (marker) marker.visible = false;
  } catch (error) {
    logger.warn("[Scene] Unexpected error during scene update", error);
  }
}

function setLastSelectionSourceScene() {
  try {
    useSelectionStore.getState().setLastSelectionSource("scene");
  } catch (error) {
    logger.warn("[Scene] Unexpected error during scene update", error);
  }
}

function handleExpectedBodyPosPickClick({
  ensurePosPickMarker,
  raycaster,
  sceneObjectsRef,
  expectedUuid,
}) {
  const bodyObj = expectedUuid ? sceneObjectsRef.current?.[expectedUuid] : null;
  if (!bodyObj) return false;

  const intersects = raycaster.intersectObjects([bodyObj], true);
  if (intersects.length > 0) {
    const bodyUuid = bodyObj.userData?.bodyUuid || expectedUuid;
    commitPosPick({ bodyObj, bodyUuid, point: intersects[0].point });
    hidePosPickMarkerFromEnsure(ensurePosPickMarker);
  }
  return true;
}

function handlePosPickFromValidIntersects({
  ensurePosPickMarker,
  getOwningBodyUuidForObject,
  posPickState,
  sceneObjectsRef,
  validIntersects,
}) {
  if (!Array.isArray(validIntersects) || validIntersects.length === 0 || !posPickState.active) {
    return false;
  }
  if (window.isRadialClickModeActive) return true;

  for (const intersect of validIntersects) {
    const bodyUuid = getOwningBodyUuidForObject(intersect.object);
    if (!bodyUuid) continue;

    const expectedUuid = posPickState.expectedBodyUuid;
    if (expectedUuid && expectedUuid !== bodyUuid) continue;

    const bodyObj = sceneObjectsRef.current?.[bodyUuid];
    if (!bodyObj) continue;

    commitPosPick({ bodyObj, bodyUuid, point: intersect.point });
    hidePosPickMarkerFromEnsure(ensurePosPickMarker);
    return true;
  }
  return true;
}

function handleScaleGizmoClick({
  canvas,
  clearHoverPreviews,
  getCollisionTarget,
  getVisualGeomTarget,
  preferPointerAfterOrbitRef,
  validIntersects,
  applyInspectCandidateSelection,
}) {
  const scaleTarget = pickScaleSelectionTargetFromIntersects(validIntersects, {
    getCollisionTarget,
    getVisualGeomTarget,
  });
  if (scaleTarget) {
    const currentSelection = useSelectionStore.getState();
    const candidateKey = scaleTarget.candidate?.name || scaleTarget.candidate?.uuid || null;
    const alreadySelected =
      (scaleTarget.targetType === "geom" && candidateKey === currentSelection.selectedGeom) ||
      (scaleTarget.targetType === "mesh" &&
        scaleTarget.candidate?.selectionType === "geom" &&
        candidateKey === currentSelection.selectedGeom) ||
      (scaleTarget.targetType === "mesh" &&
        scaleTarget.candidate?.selectionType !== "geom" &&
        candidateKey === currentSelection.selectedMesh);
    if (!alreadySelected) {
      clearHoverPreviews();
      applyInspectCandidateSelection(scaleTarget.targetType, scaleTarget.candidate);
    }
    preferPointerAfterOrbitRef.current = true;
    canvas.style.cursor = TARGET_HOVER_CURSOR;
  }
  return true;
}

function handleSavedCameraClick({
  canvas,
  clearHoverPreviews,
  preferPointerAfterOrbitRef,
  selectionActions,
  validIntersects,
}) {
  let camUuid = null;
  for (const hit of validIntersects) {
    camUuid = getSavedSceneCameraPickUuid(hit.object);
    if (camUuid) break;
  }
  if (!camUuid) return false;

  clearHoverPreviews();
  focusNewEntityInInspector("scene", () => {
    if (useSelectionStore.getState().selectedCamera !== camUuid) {
      selectionActions.selectCamera(camUuid);
    }
  });
  preferPointerAfterOrbitRef.current = true;
  canvas.style.cursor = TARGET_HOVER_CURSOR;
  return true;
}

function handleSavedLightClick({
  canvas,
  clearHoverPreviews,
  preferPointerAfterOrbitRef,
  selectionActions,
  validIntersects,
}) {
  let lightUuid = null;
  for (const hit of validIntersects) {
    lightUuid = getSavedSceneLightPickUuid(hit.object);
    if (lightUuid) break;
  }
  if (!lightUuid) return false;

  clearHoverPreviews();
  focusNewEntityInInspector("scene", () => {
    if (useSelectionStore.getState().selectedLight !== lightUuid) {
      selectionActions.selectLight(lightUuid);
    }
  });
  preferPointerAfterOrbitRef.current = true;
  canvas.style.cursor = TARGET_HOVER_CURSOR;
  return true;
}

function handlePrioritySelection({
  canvas,
  clearHoverPreviews,
  event,
  getContextTargetSnapshot,
  preferPointerAfterOrbitRef,
  positionJointSliderNextToJoint,
  showCollision,
  showConstraintMarkers,
  showInertials,
  showJointPositionSlider,
  showJoints,
  showSites,
  showVisual,
  applyInspectCandidateSelection,
}) {
  const snapshot = getContextTargetSnapshot(event);
  const flags = useViewerStore.getState().scenePrioritySelection;
  const picked = pickPriorityInspectTarget(snapshot.candidates, flags, {
    primaryTargetType: snapshot.primaryTargetType,
    showVisual,
    showJoints,
    showCollision,
    showInertials,
    showSites,
    showConstraintMarkers,
  });
  if (!picked) return false;

  clearHoverPreviews();
  if (picked.targetType === "joint" && showJointPositionSlider) {
    positionJointSliderNextToJoint(picked.candidate.uuid);
  }
  applyInspectCandidateSelection(
    picked.targetType,
    picked.candidate,
    event.metaKey || event.ctrlKey
  );
  preferPointerAfterOrbitRef.current = true;
  canvas.style.cursor = "pointer";
  return true;
}

function handleRadialClick({ event, validIntersects }) {
  if (event.button === 2) {
    window.dispatchEvent(new CustomEvent("clear-radial-seed-points"));
    return true;
  }

  const meshIntersect = validIntersects.find((intersect) => intersect.object.isMesh);
  if (!meshIntersect) return true;

  const worldPoint = meshIntersect.point.clone();
  const obj = meshIntersect.object;
  const meshName = obj.userData?.meshName;
  let bodyUuid = obj.userData?.bodyUuid;
  if (!meshName) return true;

  if (!bodyUuid) {
    let parent = obj.parent;
    while (parent && !bodyUuid) {
      if (parent.userData?.bodyUuid) {
        bodyUuid = parent.userData.bodyUuid;
        break;
      }
      parent = parent.parent;
    }
  }

  devLog("[Scene] Radial click - mesh:", meshName, "bodyUuid:", bodyUuid, "point:", worldPoint);
  window.dispatchEvent(
    new CustomEvent("radial-mesh-click", {
      detail: {
        meshName,
        bodyUuid,
        worldPoint: [worldPoint.x, worldPoint.y, worldPoint.z],
        localPoint: worldPoint.clone(),
      },
    })
  );
  return true;
}

function handlePrimaryTargetSelection({
  canvas,
  clearHoverPreviews,
  event,
  findValidTarget,
  preferPointerAfterOrbitRef,
  selectionActions,
  validIntersects,
  applyVisualMeshMultiSelection,
}) {
  for (const intersect of validIntersects) {
    if (window.isRadialClickModeActive) {
      return handleRadialClick({ event, validIntersects });
    }

    const target = findValidTarget(intersect.object);
    if (!target || target.type === "joint") continue;

    const currentSelection = useSelectionStore.getState();
    const isObjectPanelReselect =
      target.type === "body" &&
      target.uuid &&
      currentSelection.selectedBody === target.uuid &&
      (currentSelection.lastSelectionSource === "sceneTreeObject" ||
        currentSelection.lastSelectionSource === "objectLibrary" ||
        currentSelection.lastSelectionSource === "objectPropertiesLink");

    setLastSelectionSourceScene();
    clearHoverPreviews();
    if (target.type === "body") {
      if (event.metaKey || event.ctrlKey) {
        clearSelectedVisualMeshes();
        selectionActions.toggleBodyInSelection(target.uuid);
      } else if (!isObjectPanelReselect) {
        clearSelectedVisualMeshes();
        selectionActions.selectBody(target.uuid);
      }
    } else if (target.type === "mesh") {
      if (event.metaKey || event.ctrlKey) {
        applyVisualMeshMultiSelection(target);
      } else {
        clearSelectedVisualMeshes();
        selectionActions.selectMesh(target.uuid);
      }
    } else if (target.type === "geom") {
      clearSelectedVisualMeshes();
      selectionActions.selectGeom(target.name);
    } else if (target.type === "inertial") {
      clearSelectedVisualMeshes();
      selectionActions.selectInertial(target.name);
    } else if (target.type === "camera") {
      clearSelectedVisualMeshes();
      if (currentSelection.selectedCamera !== target.uuid) {
        selectionActions.selectCamera(target.uuid);
      }
      window.dispatchEvent(new CustomEvent("open-properties-panel"));
    } else if (target.type === "light") {
      clearSelectedVisualMeshes();
      if (currentSelection.selectedLight !== target.uuid) {
        selectionActions.selectLight(target.uuid);
      }
      window.dispatchEvent(new CustomEvent("open-properties-panel"));
    } else if (target.type === "actuator") {
      clearSelectedVisualMeshes();
      selectionActions.selectActuatorInParent(
        target.uuid,
        target.parentJointUuid || null,
        target.parentTendonUuid || null
      );
    }
    preferPointerAfterOrbitRef.current = true;
    canvas.style.cursor = TARGET_HOVER_CURSOR;
    return true;
  }
  return false;
}

export function handleScenePickerClick({
  applyInspectCandidateSelection,
  applyVisualMeshMultiSelection,
  camera,
  canvas,
  constraintHoveredKeyRef,
  ensurePosPickMarker,
  event,
  findValidTarget,
  getCollisionTarget,
  getContextTargetSnapshot,
  getJointIdFromObject,
  getOwningBodyUuidForObject,
  getResolvedFocusBodiesGateSet,
  getVisualGeomTarget,
  isCollisionGeomObject,
  pickConstraintMarkerKey,
  pointer,
  positionJointSliderNextToJoint,
  preferPointerAfterOrbitRef,
  raycaster,
  restoreLastCircleHover,
  sceneObjectsRef,
  sceneRef,
  selectionActions,
  setHoverPreviewBody,
  setHoverPreviewJoint,
  setHoverPreviewMesh,
  showCollision,
  showConstraintMarkers,
  showInertials,
  showJointPositionSlider,
  showJoints,
  showSavedCameraMarkers,
  showScaleGizmo,
  showSites,
  showVisual,
}) {
  const clearHoverPreviews = () =>
    clearSceneHoverPreviews({
      setHoverPreviewBody,
      setHoverPreviewJoint,
      setHoverPreviewMesh,
    });

  const rect = canvas.getBoundingClientRect();
  pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
  pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
  raycaster.setFromCamera(pointer, camera);

  if (getCirclePickState().active) {
    const circleHits = getCirclePickHits(
      raycaster.intersectObjects(sceneRef.current.children, true)
    );
    if (circleHits.length > 0) {
      handleCirclePickClick({
        hit: circleHits[0],
        restoreLastCircleHover,
        sceneObjectsRef,
      });
    }
    return true;
  }

  const visualGeomOriginPickState = getVisualGeomOriginPickState();
  if (visualGeomOriginPickState.active) {
    const hit = getVisualGeomOriginPickHit(
      raycaster.intersectObjects(sceneRef.current.children, true),
      visualGeomOriginPickState
    );
    if (hit) {
      commitVisualGeomOriginPick({
        hit,
        pickState: visualGeomOriginPickState,
        sceneObjectsRef,
      });
    }
    const marker = ensurePosPickMarker();
    if (marker) marker.visible = false;
    return true;
  }

  const posPickState = getPosPickState();
  if (posPickState.active && posPickState.expectedBodyUuid) {
    if (
      handleExpectedBodyPosPickClick({
        ensurePosPickMarker,
        expectedUuid: posPickState.expectedBodyUuid,
        raycaster,
        sceneObjectsRef,
      })
    ) {
      return true;
    }
  }

  const intersects = raycaster.intersectObjects(sceneRef.current.children, true);

  if (
    handleMeasurementPickClick({
      event,
      getOwningBodyUuidForObject,
      intersects,
    })
  ) {
    return true;
  }

  if (event.button === 0 && !window.isRadialClickModeActive) {
    const markerKey = pickConstraintMarkerKey();
    if (markerKey) {
      constraintHoveredKeyRef.current = markerKey;
      clearHoverPreviews();
      applyConstraintMarkerKeySelection(markerKey);
      preferPointerAfterOrbitRef.current = true;
      canvas.style.cursor = TARGET_HOVER_CURSOR;
      return true;
    }
    constraintHoveredKeyRef.current = null;
  }

  const focusBodiesGateSet = getResolvedFocusBodiesGateSet();
  const validIntersects = filterScenePickerIntersects(intersects, {
    focusBodiesGateSet,
    getJointIdFromObject,
    getOwningBodyUuidForObject,
    isCollisionGeomObject,
    requireVisualMeshColor: true,
    showCollision,
    showSavedCameraMarkers,
    showVisual,
  });

  if (
    handlePosPickFromValidIntersects({
      ensurePosPickMarker,
      getOwningBodyUuidForObject,
      posPickState,
      sceneObjectsRef,
      validIntersects,
    })
  ) {
    return true;
  }

  if (event.button === 0 && showScaleGizmo && !window.isRadialClickModeActive) {
    return handleScaleGizmoClick({
      applyInspectCandidateSelection,
      canvas,
      clearHoverPreviews,
      getCollisionTarget,
      getVisualGeomTarget,
      preferPointerAfterOrbitRef,
      validIntersects,
    });
  }

  if (validIntersects.length === 0) {
    if (!getResolvedFocusBodiesGateSet()) {
      setHoverPreviewBody(null);
      setHoverPreviewJoint(null);
      selectionActions.clearAllSelections();
    }
    return true;
  }

  if (event.button === 0 && !window.isRadialClickModeActive) {
    if (
      handleSavedCameraClick({
        canvas,
        clearHoverPreviews,
        preferPointerAfterOrbitRef,
        selectionActions,
        validIntersects,
      })
    ) {
      return true;
    }
    if (
      handleSavedLightClick({
        canvas,
        clearHoverPreviews,
        preferPointerAfterOrbitRef,
        selectionActions,
        validIntersects,
      })
    ) {
      return true;
    }
    if (
      handlePrioritySelection({
        applyInspectCandidateSelection,
        canvas,
        clearHoverPreviews,
        event,
        getContextTargetSnapshot,
        positionJointSliderNextToJoint,
        preferPointerAfterOrbitRef,
        showCollision,
        showConstraintMarkers,
        showInertials,
        showJointPositionSlider,
        showJoints,
        showSites,
        showVisual,
      })
    ) {
      return true;
    }
  }

  if (event.button === 0 && showJoints && !window.isRadialClickModeActive) {
    const centerJointTarget = pickJointCenterProxyHit(validIntersects, findValidTarget);
    if (centerJointTarget) {
      setLastSelectionSourceScene();
      clearHoverPreviews();
      if (showJointPositionSlider) {
        positionJointSliderNextToJoint(centerJointTarget.uuid);
      }
      selectionActions.selectJoint(centerJointTarget.uuid);
      preferPointerAfterOrbitRef.current = true;
      canvas.style.cursor = TARGET_HOVER_CURSOR;
      return true;
    }
  }

  if (
    handlePrimaryTargetSelection({
      applyVisualMeshMultiSelection,
      canvas,
      clearHoverPreviews,
      event,
      findValidTarget,
      preferPointerAfterOrbitRef,
      selectionActions,
      validIntersects,
    })
  ) {
    return true;
  }

  if (showJoints) {
    const jointTarget = pickBestJointHit(validIntersects, findValidTarget);
    if (jointTarget && jointTarget.type === "joint") {
      setLastSelectionSourceScene();
      clearHoverPreviews();
      if (showJointPositionSlider) {
        positionJointSliderNextToJoint(jointTarget.uuid);
      }
      selectionActions.selectJoint(jointTarget.uuid);
      preferPointerAfterOrbitRef.current = true;
      canvas.style.cursor = TARGET_HOVER_CURSOR;
      return true;
    }
  }

  setHoverPreviewBody(null);
  setHoverPreviewJoint(null);
  selectionActions.clearAllSelections();
  return true;
}
